/**
 * How far the nearest land is, from anywhere at sea — the one piece of data an
 * ocean shader needs that no amount of arithmetic in the fragment can supply.
 *
 * `three/ocean-material.ts` uses it for the shoal band: water walks up
 * `WATER_RAMP` as the bottom comes up under it, which is what the user's word
 * *refraction* actually looks like from a cockpit at altitude. A literal
 * refraction is a scene-colour copy and therefore a second render target, which
 * this port does not have and is not going to grow for a tint
 * (`docs/engineering/ocean.md` §3).
 *
 * ## It is the water mask's own rule, rasterised instead of point-queried
 *
 * `water-mask.ts` settles "is this point at sea" as *the last triangle covering
 * it in `.WLD` draw order is `anchorIndex === 4`* — a topmost-surface test, not
 * a coverage test, and the module doc there has the evidence for why. This file
 * answers the same question for a whole grid at once by painting the same
 * triangles into it in the same order and letting the last one win, which is
 * what a 1990 renderer with no depth buffer did to the screen and what makes the
 * two agree **by construction**: a scanline is evaluated at the row's centre and
 * filled by cell centre, so every cell of this grid holds exactly `isWater` at
 * that cell's centre. `shore-field.test.ts` samples the real bake and asserts
 * that equality rather than trusting the paragraph.
 *
 * Doing it as a rasterisation and not as `size²` calls to `isWater` is a factor
 * of about forty: the mask's own bucketed query is a few dozen triangle tests,
 * and a region's handful of enormous plates cover the whole grid, so a
 * `fill()` per scanline replaces a point-in-triangle per cell.
 *
 * ## Why a window rather than the whole plate
 *
 * London's sea plate is 936 x 607 km and `flight-view.ts`'s far plane is 26 km,
 * so a field over the plate's own bounds would spend almost all of its
 * resolution on water no sortie ever sees. `SHORE_FIELD_SPAN_M` is a window
 * about the region's origin — the start aerodrome — sized for a patrol's real
 * range rather than the plate's, and outside it the field reads deep, which is
 * the right answer for open sea and the honest one for anywhere else.
 */

import type { SurfaceTriangle } from "./water-mask.ts";

/** Cells across the square field. 1 024 at the span below is 156 m a cell — finer than the coastline's own detail in the bake. */
export const SHORE_FIELD_CELLS = 1024;

/**
 * The window's side in metres, centred on the region origin. 160 km is a little
 * over three times `flight-view.ts`'s 26 km far plane in every direction, which
 * covers a patrol's real range (Dunkirk's ship circuits start 31 km out) without
 * spending resolution on water the far plane never draws.
 */
export const SHORE_FIELD_SPAN_M = 160_000;

/**
 * How far from land the shoal band reaches, metres — the distance the encoded
 * byte saturates at.
 *
 * 2.5 km. Sized to be *seen*, which is the constraint that matters: at 6 000 ft
 * a 2.5 km band along a coastline is tens of pixels wide from any sane angle,
 * where a physically-honest 200 m one would be sub-pixel and the feature would
 * be invisible in exactly the condition it was built for. Declared as scenery
 * rather than bathymetry — the bake carries no depth soundings and this port
 * invents none.
 */
export const SHORE_BAND_M = 2500;

/** Metres a cell covers. */
export const SHORE_CELL_M = SHORE_FIELD_SPAN_M / SHORE_FIELD_CELLS;

/**
 * The field, as the texture wants it: one byte a cell, `0` at the waterline and
 * `255` at `SHORE_BAND_M` and beyond.
 */
export interface ShoreField {
  /** `cells x cells`, row-major, row 0 at `minZ`. */
  readonly data: Uint8Array;
  readonly cells: number;
  /** World metres (recentred frame) of the field's corner and its side. */
  readonly minX: number;
  readonly minZ: number;
  readonly spanM: number;
  /** What a byte of 255 means, metres. */
  readonly bandM: number;
  /** Cells the topmost-surface test called water — reported rather than assumed, and what the tests pin. */
  readonly waterCells: number;
}

/** Chamfer weights for the two-pass distance transform, in units of a third of a cell — the standard (3, 4) pair, ~5 % worst-case error against Euclidean. */
const STRAIGHT = 3;
const DIAGONAL = 4;
/** A land cell's distance, and the initial value of everything else. Large enough that no path can reach it, small enough to stay well inside `Int32Array`. */
const UNREACHED = 1 << 28;

/** Water/land per cell, by painting every surface triangle in draw order and letting the last one win. */
function paintSurface(triangles: readonly SurfaceTriangle[], minX: number, minZ: number, cells: number): Uint8Array {
  const water = new Uint8Array(cells * cells);
  const xs: number[] = [];
  for (const triangle of triangles) {
    const value = triangle[6] === 1 ? 1 : 0;
    const lowZ = Math.min(triangle[1], triangle[3], triangle[5]);
    const highZ = Math.max(triangle[1], triangle[3], triangle[5]);
    // Rows whose *centre* falls inside the triangle's z span — the same sample
    // points `isWater` would be asked about.
    const firstRow = Math.max(0, Math.ceil((lowZ - minZ) / SHORE_CELL_M - 0.5));
    const lastRow = Math.min(cells - 1, Math.floor((highZ - minZ) / SHORE_CELL_M - 0.5));
    const corners: readonly (readonly [number, number])[] = [
      [triangle[0], triangle[1]],
      [triangle[2], triangle[3]],
      [triangle[4], triangle[5]],
    ];
    for (let row = firstRow; row <= lastRow; row += 1) {
      const z = minZ + (row + 0.5) * SHORE_CELL_M;
      xs.length = 0;
      for (let edge = 0; edge < 3; edge += 1) {
        const [ax, az] = corners[edge]!;
        const [bx, bz] = corners[(edge + 1) % 3]!;
        if (az === bz) {
          if (az === z) xs.push(ax, bx);
          continue;
        }
        if (z < Math.min(az, bz) || z > Math.max(az, bz)) continue;
        xs.push(ax + ((z - az) / (bz - az)) * (bx - ax));
      }
      if (xs.length === 0) continue;
      const left = Math.max(0, Math.ceil((Math.min(...xs) - minX) / SHORE_CELL_M - 0.5));
      const right = Math.min(cells - 1, Math.floor((Math.max(...xs) - minX) / SHORE_CELL_M - 0.5));
      if (right >= left) water.fill(value, row * cells + left, row * cells + right + 1);
    }
  }
  return water;
}

/** Two-pass chamfer: every cell's distance to the nearest land cell, in thirds of a cell. */
function distanceToLand(water: Uint8Array, cells: number): Int32Array {
  const distance = new Int32Array(cells * cells);
  for (let at = 0; at < distance.length; at += 1) distance[at] = water[at] === 1 ? UNREACHED : 0;
  const relax = (at: number, from: number, cost: number): void => {
    const candidate = distance[from]! + cost;
    if (candidate < distance[at]!) distance[at] = candidate;
  };
  for (let row = 0; row < cells; row += 1) {
    for (let column = 0; column < cells; column += 1) {
      const at = row * cells + column;
      if (distance[at] === 0) continue;
      if (row > 0) relax(at, at - cells, STRAIGHT);
      if (column > 0) relax(at, at - 1, STRAIGHT);
      if (row > 0 && column > 0) relax(at, at - cells - 1, DIAGONAL);
      if (row > 0 && column < cells - 1) relax(at, at - cells + 1, DIAGONAL);
    }
  }
  for (let row = cells - 1; row >= 0; row -= 1) {
    for (let column = cells - 1; column >= 0; column -= 1) {
      const at = row * cells + column;
      if (distance[at] === 0) continue;
      if (row < cells - 1) relax(at, at + cells, STRAIGHT);
      if (column < cells - 1) relax(at, at + 1, STRAIGHT);
      if (row < cells - 1 && column < cells - 1) relax(at, at + cells + 1, DIAGONAL);
      if (row < cells - 1 && column > 0) relax(at, at + cells - 1, DIAGONAL);
    }
  }
  return distance;
}

/**
 * The field for a region, in the same recentred frame the scenery is built in.
 *
 * `triangles` is `water-mask.ts`'s own `surfaceTrianglesOf` output — the same
 * array, in the same draw order, so this cannot disagree with the predicate the
 * sim's `GroundQuery` and the aerodrome anchoring both read.
 */
export function buildShoreField(
  triangles: readonly SurfaceTriangle[],
  cells: number = SHORE_FIELD_CELLS,
  spanM: number = SHORE_FIELD_SPAN_M,
  bandM: number = SHORE_BAND_M,
): ShoreField {
  const minX = -spanM / 2;
  const minZ = -spanM / 2;
  const water = paintSurface(triangles, minX, minZ, cells);
  const distance = distanceToLand(water, cells);
  const data = new Uint8Array(cells * cells);
  // Thirds of a cell, because `STRAIGHT` is 3 — so a chamfer step of 3 is one
  // cell of `SHORE_CELL_M`.
  const metresPerUnit = (spanM / cells) / STRAIGHT;
  let waterCells = 0;
  for (let at = 0; at < data.length; at += 1) {
    if (water[at] === 1) waterCells += 1;
    data[at] = Math.round(255 * Math.min(1, (distance[at]! * metresPerUnit) / bandM));
  }
  return { data, cells, minX, minZ, spanM, bandM, waterCells };
}

/** The shoal fraction at a point — `1` at the waterline, `0` at `bandM` and beyond. The scalar twin of the shader's fetch, for the tests. */
export function shoalAt(field: ShoreField, x: number, z: number): number {
  const column = Math.min(field.cells - 1, Math.max(0, Math.floor((x - field.minX) / (field.spanM / field.cells))));
  const row = Math.min(field.cells - 1, Math.max(0, Math.floor((z - field.minZ) / (field.spanM / field.cells))));
  return 1 - (field.data[row * field.cells + column]! / 255);
}

